import {cascadingGet, getIfNotEmpty, isEmpty, isNil} from '@megaphonenpm/mputils/src/utils.js';
import {MegaScreen} from "../MegaScreen.js";
import {Component} from '@megaphonenpm/mpui/src/Component.js';

/**
 * Base class for all MegaScreen components.
 */
export class ScreenComponent extends Component {
  constructor (themeID, idsOfRequiredElements = [], props = {}) {
    // The props supplied in this super() call will be converted to instance variables by the
    // Component superconstructor.
    super({themeID: themeID,
      idsOfRequiredElements: idsOfRequiredElements,
      theme: MegaScreen.globals.themeService.getTheme(themeID),
      ...props});
  }

  validate () {
    this.valid = this.validateIDs(this.idsOfRequiredElements);
  }

  validateIDs (idsOfRequiredElements) {
    let valid = true;
    if (isNil(idsOfRequiredElements)) {
      valid = false;
    } else {
      idsOfRequiredElements.forEach(id => {
        if (isNil(MegaScreen.globals.themeLoader.getThemeDiv(this.themeID).querySelector(`#${id}`))) {
          valid = false;
        }
      });
    }

    return valid;
  }

  build () {
    super.build();

    // Make sure all required elements are present in the theme. If validation fails, subclasses can
    // choose to abort build() in their overridden versions.
    this.validate();
  }

  /**
   * Generic theme-setting retrieval utility providing a more readable wrapper for get.
   *
   * @param path The full path to the desired setting. Example: 'design.products.moodmeter.screen.align.vertical'
   * @param defaultValue The value to return if the requested setting is undefined.
   * @returns The value of the setting specified by path.
   */
  getThemeSetting (path, defaultValue) {
    return getIfNotEmpty(this.theme, path, defaultValue);
  }

  /**
   * Returns the value of the specified "cascading theme setting". A "cascading theme setting" is
   * a theme setting that can be ovverriden at multiple levels, as follows:
   *   multichoice product > multichoice default > product > state > default
   *
   * Example:
   *   this.cascadingGetThemeSetting('media.question.align.horizontal', productID, productName,'scene');
   *
   * @param path The partial path to the desired setting. Example: 'media.question.align.horizontal'
   * @param productID The id of the product for which the setting can be overridden.
   * @param productName The name of the product for which the setting can be overridden.
   * @param stateName The name of the state for which the setting can be overridden.
   * @param defaultValue The value to return if the requested setting is undefined.
   *
   * @returns The value of the setting specified by path.
   */
  // Cascade setting value: multichoice procuct > multichoice default > product > state > default
  cascadingGetThemeSetting (path, productID, productName, stateName, defaultValue) {
    let paths = [];
    if (MegaScreen.globals.productService.isMultichoiceProduct(productName)) {
      paths.push(`design.products.multichoice_products.screen.${productID}.${path}`,
        `design.products.multichoice_products.screen.default.${path}`);
    } else if (MegaScreen.globals.productService.isTitleProduct(productName)) {
      paths.push(`design.products.title_products.screen.${path}`);
    } else if (MegaScreen.globals.productService.isSocialProduct(productName)) {
      paths.push(`design.products.social_products.screen.${path}`);
    }
    paths.push(`design.products.${productName}.screen.${path}`,
      `design.states.screen.${stateName}.${path}`,
      `design.default.screen.${path}`);

    return cascadingGet(this.theme, paths, defaultValue);
  }

  /**
   * Returns the value of the locale string for the specified setting (name), in the current locale.
   *
   * Example:
   * // The following code requests the "locale string" for the "points_suffix" setting. When the
   * // current locale is set to 'en' (e.g., by a playlist setting), then the following code typically
   * // returns 'pts'. Some themes, however, might return 'points' or another custom value for
   * // 'points_suffix' in English.
   * this.getLocaleString('points_suffix');
   *
   * @param name The name of the locale setting to retrieve.
   */
  getLocaleString (name) {
    // Check playlist settings for current locale
    let currentLocale = getIfNotEmpty(MegaScreen.globals.dataStore.playlist, 'defaults.presentation.locale.screen');
    if (isEmpty(currentLocale)) {
      // No playlist locale specified, so check deployment settings for current locale
      currentLocale = getIfNotEmpty(MegaScreen.globals.dataStore.deploymentSettings, 'defaults.presentation.locale.screen');
    }
    if (isEmpty(currentLocale)) {
      // Locale not specified in playlist settings or deployment settings, so default to English
      currentLocale = 'en';
    }
    // If the locale setting isn't found, return the empty string '' (don't return null because null
    // converts to the string "null", which could result in the word null appearing on screen. For example,
    // if "pts" isn't available in the current locale, display 999 not 999null).
    return this.getThemeSetting(`locale.${currentLocale}.screen.${name}`, '');
  }
}
